import { useMemo } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { NotificationManager } from '@/components/Notification'
import { WishlistService } from '@/services'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import { selectUserId } from '@/store/slices/authenticationSlice'
import { selectHasSessionBasedShoppingCart } from '@/store/selectors/userSelector'
import { getCartItems } from '@/store/slices/cartSlice'
import { usePunchoutContext } from '@/contexts/punchout/usePunchoutContext'
import { PAGE_SIZE } from '@/libs/constants'

export const WISHLIST_DETAIL_PAGE_SIZE = PAGE_SIZE[12]

export interface WishlistDetails {
  wishlistOwnerId: string
  wishlistId: string
  ownerId: string
  ownerName: string
  wishlistName: string
  isShared: boolean
  items: {
    items: any[]
  }
  totalProduct: number
}

interface GetWishlistDetailsParams {
  wishlistOwnerId: string
  wishlistId: string
  pageNumber?: number
  pageSize?: number
  searchQuery?: string
  enabled?: boolean
}

interface UpdateProductQuantityParams {
  wishlistOwnerId: string
  wishlistId: string
  productData: {
    materialNumber: string
    quantity: number
  }
}

interface AddAllProductsToCartParams {
  wishlistOwnerId: string
  wishlistId: string
}

export const wishlistKeys = {
  all: ['wishlists'] as const,
  lists: () => [...wishlistKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...wishlistKeys.lists(), filters] as const,
  details: () => [...wishlistKeys.all, 'detail'] as const,
  detail: (id: string, filters?: Record<string, any>) =>
    filters
      ? ([...wishlistKeys.details(), id, filters] as const)
      : ([...wishlistKeys.details(), id] as const),
}

export const useFetchWishlistDetails = ({
  wishlistOwnerId,
  wishlistId,
  pageNumber = 1,
  pageSize = WISHLIST_DETAIL_PAGE_SIZE,
  searchQuery = '',
  enabled = true,
}: GetWishlistDetailsParams) => {
  return useQuery({
    queryKey: wishlistKeys.detail(wishlistId, {
      wishlistOwnerId,
      pageNumber,
      pageSize,
      searchQuery,
    }),
    queryFn: async (): Promise<WishlistDetails> => {
      const { data } = await WishlistService.getWishlistDetail({
        wishlistOwnerId,
        wishlistId,
        pageNumber,
        pageSize,
        searchQuery,
      })
      return data as WishlistDetails
    },
    enabled: Boolean(wishlistOwnerId && wishlistId) && enabled,
    placeholderData: keepPreviousData,
  })
}

export const useUpdateProductQuantity = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      wishlistOwnerId,
      wishlistId,
      productData,
    }: UpdateProductQuantityParams) => {
      const response = await WishlistService.updateWishlistProductQuantity({
        wishlistOwnerId,
        wishlistId,
        productData,
      })
      return response.data
    },
    onMutate: async ({ wishlistId, productData }) => {
      await queryClient.cancelQueries({
        queryKey: wishlistKeys.detail(wishlistId),
      })

      const previousData = queryClient.getQueryData(
        wishlistKeys.detail(wishlistId),
      )

      queryClient.setQueriesData(
        { queryKey: wishlistKeys.detail(wishlistId) },
        (old: any) => {
          if (!old?.items?.items) return old

          return {
            ...old,
            items: {
              ...old.items,
              items: old.items.items.map((item: any) =>
                item.materialNumber === productData.materialNumber
                  ? { ...item, quantity: productData.quantity }
                  : item
              ),
            },
          }
        },
      )

      return { previousData }
    },
    onError: (_error, { wishlistId }, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          wishlistKeys.detail(wishlistId),
          context.previousData,
        )
      }
      NotificationManager.error({
        message: 'notification.error.updateWishListProduct',
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: wishlistKeys.lists(),
      })
    },
  })
}

export const useAddAllProductsToCart = () => {
  const dispatch = useAppDispatch()
  const { sessionId } = usePunchoutContext() || {}
  const hasSessionBasedShoppingCart = useAppSelector(selectHasSessionBasedShoppingCart)

  return useMutation({
    mutationFn: async ({
      wishlistOwnerId,
      wishlistId,
    }: AddAllProductsToCartParams) => {
      const response = await WishlistService.addWishlistProductsToCart({
        wishlistOwnerId,
        wishlistId,
        sessionId: hasSessionBasedShoppingCart ? sessionId : undefined,
      })
      return response.data
    },
    onSuccess: (data) => {
      if (data === true) {
        NotificationManager.error({
          message: 'notification.error.addCartMandatorySafetyInstruction',
        })
        return
      }

      dispatch(getCartItems())

      NotificationManager.success({
        message: 'notification.success.addProduct',
      })
    },
    onError: (error) => {
      NotificationManager.error({
        message: 'notification.error.addProduct',
        description: String(error),
      })
    },
  })
}

export const useIsSharedWishlist = (
  wishlistOwnerId: string,
  wishlistId: string,
  ownerIdFromCaller?: string,
) => {
  const currentUserId = useAppSelector(selectUserId)
  const { data: wishlistDetails } = useFetchWishlistDetails({
    wishlistOwnerId,
    wishlistId,
    enabled: !ownerIdFromCaller,
  })

  return useMemo(() => {
    const ownerId = ownerIdFromCaller ?? wishlistDetails?.ownerId

    return Boolean(
      (ownerId && ownerId !== currentUserId)
        || (wishlistOwnerId && currentUserId && wishlistOwnerId !== currentUserId),
    )
  }, [ownerIdFromCaller, wishlistDetails?.ownerId, wishlistOwnerId, currentUserId])
}

interface RenameWishlistParams {
  ownerId: string
  wishlistId: string
  newName: string
}

export const useRenameWishlist = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      ownerId,
      wishlistId,
      newName,
    }: RenameWishlistParams) => {
      const response = await WishlistService.renameWishlist({
        ownerId,
        wishlistId,
        newName,
      })
      return response.data
    },
    onSuccess: (_data, variables) => {
      // Invalidate and refetch wishlist details to get updated name
      void queryClient.invalidateQueries({
        queryKey: wishlistKeys.detail(variables.wishlistId),
      })

      // Also invalidate the overview list in case it's displayed
      void queryClient.invalidateQueries({
        queryKey: wishlistKeys.lists(),
      })
    },
    onError: (error) => {
      NotificationManager.error({
        message: 'notification.error.editWishlist',
        description: String(error),
      })
    },
  })
}

interface RelocateWishlistItemParams {
  materialNumber: string
  order: number
}

export const useRelocateWishlistItems = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      ownerId,
      wishlistId,
      items,
    }: {
      ownerId: string
      wishlistId: string
      items: RelocateWishlistItemParams[]
    }) => {
      await WishlistService.relocateWishlistItems(ownerId, wishlistId, items)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: wishlistKeys.detail(variables.wishlistId),
      })
    },
    onError: () => {
      NotificationManager.error({
        message: 'notification.error.relocateWishlists',
      })
    },
  })
}
